import { jwtStore } from "../auth/jwtStore";
import { authzStore } from "../authz/authzStore";
import { projectStore } from "../project/projectStore";

/**
 * Single chokepoint for all patient-registry API calls.
 *
 * PRD-002 DoD enforcement:
 * - Every call sets `Authorization: Bearer <jwt>`.
 * - Calls without a JWT in app state surface the paste modal before any fetch.
 * - 401 responses clear the cached JWT and re-surface the modal.
 *
 * PRD-004 DoD enforcement:
 * - Every project-scoped call sets the `ProjectId` request header from the
 *   selected project store. The `/auth/identity` path is exempt — it is
 *   the pre-project-selection call (PRD-003 / PRD-004).
 * - Calls that require a project but have none selected throw
 *   `ProjectRequiredError` rather than silently issuing a scoped call.
 *
 * Static-analysis CI (`tools/no-direct-fetch.sh`) ensures that `fetch` calls
 * to `/api/patient-registry/...` do not appear anywhere outside this module.
 */

export class AuthRequiredError extends Error {
    constructor(reason: "no-jwt" | "rejected") {
        super(reason === "no-jwt" ? "no jwt cached" : "jwt rejected by patient-registry");
        this.name = "AuthRequiredError";
    }
}

export class ProjectRequiredError extends Error {
    constructor() {
        super("no project selected; this endpoint requires a ProjectId header");
        this.name = "ProjectRequiredError";
    }
}

/**
 * Paths that the patient-registry serves WITHOUT requiring a `ProjectId`
 * header. PRD-003 confirmed `/auth/identity` is the only such path used
 * by this app. New entries here are scope-shaping and should land
 * alongside a PRD revision.
 */
const PROJECTLESS_PATHS = new Set<string>(["/auth/identity"]);

let openModalCallback: () => void = () => {};

export function setOpenModalCallback(fn: () => void): void {
    openModalCallback = fn;
}

export interface PatientRegistryFetchOptions extends Omit<RequestInit, "headers"> {
    headers?: Record<string, string>;
}

export async function patientRegistryFetch(path: string, init: PatientRegistryFetchOptions = {}): Promise<Response> {
    const jwt = jwtStore.get();
    if (!jwt) {
        openModalCallback();
        throw new AuthRequiredError("no-jwt");
    }

    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    const requiresProject = !PROJECTLESS_PATHS.has(normalizedPath);

    const headers: Record<string, string> = {
        ...(init.headers ?? {}),
        Authorization: `Bearer ${jwt}`,
    };

    if (requiresProject) {
        const projectId = projectStore.get();
        if (!projectId) {
            throw new ProjectRequiredError();
        }
        headers.ProjectId = projectId;
    }

    const url = `/api/patient-registry${normalizedPath}`;
    const response = await fetch(url, { ...init, headers });
    if (response.status === 401) {
        jwtStore.clear();
        openModalCallback();
        throw new AuthRequiredError("rejected");
    }
    // PRD-005 panel security review (trinity I-1): only the locked
    // "forbidden" discriminator our edge emits means "Subject_Read missing
    // → deny page." A 403 with any other discriminator (or no body) comes
    // from upstream patient-registry's own per-resource permission gates
    // (project ACL, cohort scope) and must NOT bounce the user to the deny
    // page — that would mis-guide a CSM whose tenant admin already granted
    // Subject_Read into a useless re-paste loop. Clone the response so
    // callers downstream can still read the body.
    if (response.status === 403) {
        const clone = response.clone();
        try {
            const body = (await clone.json()) as { error?: unknown };
            if (body && body.error === "forbidden") {
                authzStore.setDenied();
            }
        } catch {
            // Body not JSON / not parseable — assume upstream 403, no deny.
        }
    }
    return response;
}
